import { useScrollReveal } from '@/hooks/useScrollReveal';

const tiers = [
  {
    name: 'Verified',
    icon: 'ri-shield-check-line',
    color: 'text-primary-600',
    bgColor: 'bg-primary-50',
    borderColor: 'border-primary-200/70',
    badgeBg: 'bg-primary-100 text-primary-700',
    description: 'Basic verification for all Freshhlock Wholesale participants. KYC-completed, trade-reference checked, and operational audit passed. This is the baseline trust tier — every buyer and supplier on the platform is at minimum Verified.',
    checks: ['KYC & business registration verified', 'Trade references validated', 'Operational facility audit completed', 'Monthly quality spot-checks'],
  },
  {
    name: 'HACCP-Certified',
    icon: 'ri-award-line',
    color: 'text-secondary-600',
    bgColor: 'bg-secondary-50',
    borderColor: 'border-secondary-200/70',
    badgeBg: 'bg-secondary-100 text-secondary-700',
    description: 'Suppliers who hold valid HACCP certification for their processing or storage facility. This tier appears on product listings and spec sheets, giving buyers an additional layer of food-safety confidence.',
    checks: ['Valid HACCP certification on file', 'Critical control points documented', 'Temperature logs reviewed quarterly', 'Third-party audit verification'],
  },
  {
    name: 'Export-Grade',
    icon: 'ri-global-line',
    color: 'text-accent-600',
    bgColor: 'bg-accent-50',
    borderColor: 'border-accent-200/70',
    badgeBg: 'bg-accent-100 text-accent-700',
    description: 'The highest trust tier — reserved for suppliers meeting international export standards including EU and US FDA compliance. Export-Grade products carry the strongest quality guarantee on the platform.',
    checks: ['Export registration & licensing verified', 'EU / US FDA compliance documentation', 'Residue & contaminant testing quarterly', 'Full batch-level traceability system'],
  },
];

export default function VerificationTiers() {
  const { ref, revealed } = useScrollReveal();

  return (
    <section className="py-20 md:py-28 warm-sand-bg">
      <div ref={ref} className={`container-page scroll-reveal ${revealed ? 'revealed' : ''}`}>
        <div className="text-center mb-14 md:mb-20">
          <span className="inline-block text-xs font-semibold text-accent-600 uppercase tracking-widest mb-4">
            Trust & Verification
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-primary-700 mb-4">
            Three Tiers of Trust. One Standard of Quality.
          </h2>
          <p className="text-foreground-500 max-w-2xl mx-auto">
            Every product on Freshhlock Wholesale carries a verification badge — so you know exactly what level of assurance sits behind each listing.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {tiers.map((tier) => (
            <div
              key={tier.name}
              className={`group bg-card-50 rounded-xl p-6 md:p-8 border ${tier.borderColor} shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 cursor-pointer card-animate`}
            >
              <div className={`w-12 h-12 flex items-center justify-center rounded-xl ${tier.bgColor} ${tier.color} mb-5 animate-icon-float icon-hover-pop`}>
                <i className={`${tier.icon} text-xl`}></i>
              </div>

              <div className="flex items-center gap-3 mb-4">
                <h3 className="font-heading text-lg font-bold text-primary-700">{tier.name}</h3>
                <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${tier.badgeBg}`}>
                  Badge
                </span>
              </div>

              <p className="text-sm text-foreground-500 leading-relaxed mb-5">
                {tier.description}
              </p>

              <ul className="space-y-2">
                {tier.checks.map((check) => (
                  <li key={check} className="flex items-start gap-2 text-sm text-foreground-600">
                    <i className={`ri-check-line ${tier.color} mt-0.5 flex-shrink-0`}></i>
                    <span>{check}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <p className="text-sm text-foreground-400">
            All verification tiers are audited and renewed every 6 months.{' '}
            <span className="text-secondary-600 font-semibold cursor-pointer hover:text-secondary-500 transition-colors">
              Learn more about our trust system →
            </span>
          </p>
        </div>
      </div>
    </section>
  );
}